import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { usePublisherAuth } from '../../context/PublisherAuthContext'
import {
  Package,
  Plus,
  Search,
  Link2,
  Eye,
  CheckCircle,
  Clock,
  AlertTriangle,
  Settings,
  ExternalLink,
} from 'lucide-react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface PackageItem {
  package_id: string
  slug: string
  name: string
  summary: string
  latest_version: string
  review_status: string
  connection_count: number
  view_count: number
  created_at: string
  updated_at: string
}

export default function PublisherPackagesPage() {
  const { token } = usePublisherAuth()
  const [packages, setPackages] = useState<PackageItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  useEffect(() => {
    loadPackages()
  }, [token])

  const loadPackages = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/v1/publisher/me/packages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      if (!res.ok) {
        setPackages([])
        return
      }
      const data = await res.json()
      if (Array.isArray(data)) {
        setPackages(data.map((pkg: any) => ({
          package_id: pkg.package_id || '',
          slug: pkg.slug || '',
          name: pkg.name || '',
          summary: pkg.summary || '',
          latest_version: pkg.latest_version || '0.0.0',
          review_status: pkg.review_status || 'draft',
          connection_count: pkg.connection_count || 0,
          view_count: pkg.view_count || 0,
          created_at: pkg.created_at || '',
          updated_at: pkg.updated_at || '',
        })))
      } else {
        setPackages([])
      }
    } catch {
      setPackages([])
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </span>
        )
      case 'submitted':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            In Review
          </span>
        )
      case 'changes_requested':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Changes Requested
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Draft
          </span>
        )
    }
  }

  const filteredPackages = packages.filter((pkg) => {
    const matchesSearch =
      pkg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pkg.slug.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || pkg.review_status === statusFilter
    return matchesSearch && matchesStatus
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Packages</h1>
          <p className="text-gray-500 mt-1">Manage your MCP skill packages</p>
        </div>
        <Link to="/publisher/packages/new" className="btn btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          New Package
        </Link>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search packages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-full"
            >
              <option value="all">All statuses</option>
              <option value="approved">Approved</option>
              <option value="submitted">In Review</option>
              <option value="changes_requested">Changes Requested</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
      </div>

      {/* Packages list */}
      <div className="card">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-gray-500">Loading packages...</p>
          </div>
        ) : filteredPackages.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {packages.length === 0 ? 'No packages yet' : 'No packages found'}
            </h3>
            <p className="text-gray-500 mb-4">
              {packages.length === 0
                ? 'Create your first MCP skill package to get started.'
                : 'Try adjusting your search or filter criteria.'}
            </p>
            {packages.length === 0 && (
              <Link to="/publisher/packages/new" className="btn btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Create Package
              </Link>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredPackages.map((pkg) => (
              <div key={pkg.package_id} className="p-5 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <Link
                        to={`/publisher/packages/${pkg.package_id}`}
                        className="text-lg font-medium text-gray-900 hover:text-primary-600"
                      >
                        {pkg.name}
                      </Link>
                      {getStatusBadge(pkg.review_status)}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{pkg.summary}</p>
                    <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                        {pkg.slug}
                      </span>
                      <span>v{pkg.latest_version}</span>
                      <span className="flex items-center">
                        <Link2 className="w-4 h-4 mr-1" />
                        {pkg.connection_count.toLocaleString()}
                      </span>
                      <span className="flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        {pkg.view_count.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Link
                      to={`/skills/${pkg.slug}`}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                      title="View public page"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </Link>
                    <Link
                      to={`/publisher/packages/${pkg.package_id}/settings`}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                      title="Settings"
                    >
                      <Settings className="w-5 h-5" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
